import Loading from '@mortgage-pos/ui/components/Loading'
import RatesVariation from './ratesPages/RatesVariation'
import RatesPage from './ratesPages/Rates'
import { useNavigate } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { BankrateRate, Routes, Status } from '@mortgage-pos/types'
import incense from '@mortgage-pos/ui/incense'
import { environment } from '@mortgage-pos/ui/env'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import { translateAnswersToCPLFormat } from '@mortgage-pos/ui/utils/bankrateCpl'

interface RateSelectionProps {
  modifierClass?: string
}

const CompareLERateSelection = ({
  modifierClass = '--compare',
}: RateSelectionProps) => {
  const navigate = useNavigate()
  const [hasRequestedRates, setHasRequestedRates] = useState<boolean>(false)
  const [noRatesVariation, setNoRatesVariation] = useState<
    'noRates' | 'greatRates' | 'cashOut'
  >('noRates')
  const [maxCashOutAmount, setMaxCashOutAmount] = useState<number>(0)

  const showCompareDemo = useVariation(
    FEATURE_FLAG_TYPES.ENABLE_COMPARE_LE_DEMO
  )

  const showCashOutVariation = useVariation(
    FEATURE_FLAG_TYPES.BANKRATE_COMPARE_CASHOUT_VARIATION
  )

  const alvinRedirectEnabled = useVariation(
    FEATURE_FLAG_TYPES.ALVIN_CPL_REDIRECT
  )

  const { mergedAnswers } = useStoreState((state) => state.answers)
  const {
    sortedBankrateRates,
    bankrateRates,
    equityInHome,
    currentMortgageDetails,
  } = useStoreState((state) => state.bankrateCompare)

  const { requestBankrateRates, submitSageLead, setSelectedBankrateRate } =
    useStoreActions((state) => state.bankrateCompare)
  const { formSubmitted } = useStoreActions((state) => state.tracking)
  const { updateStatus } = useStoreActions((state) => state.application)

  const submitRate = async (rate: BankrateRate) => {
    try {
      await setSelectedBankrateRate(rate)
      formSubmitted()
    } catch (err) {
      incense(err)
        .details({
          name: 'submitRateFailure',
          message: 'Failed to submit rate',
        })
        .sensitive({ rate })
        .error()
      navigate(`/${Routes.APP_1003}/error`)
    }

    let redirectUrl

    // This ensures Sage leads get dropped onto Sage Alfie. All others to Mortgage Lead Form
    if (rate.bankrateAdvertiser.nmlsLicense === '1374724') {
      let leadResponse
      try {
        leadResponse = await submitSageLead({ rate, cashout: maxCashOutAmount })
      } catch (err) {
        incense(err)
          .details({
            name: 'SubmitSageLeadFailure',
            message: 'Error submitting lead to Sage API',
          })
          .sensitive({ rate })
          .error()
        navigate(`/${Routes.APP_1003}/error`)
      }

      redirectUrl = `${environment.sageQuestionnaireBaseUrl}/application-1003?question-set=ntl&application-guid=${leadResponse}`
    } else {
      // Tracking link with qa. inserted for proper testing route
      const trackingLinkWithQa = rate.trackingLink.split('ep.').join('ep.qa.')
      const leadParams = new URLSearchParams()

      leadParams.append('show_15S2S_content', 'true')

      const urlParams = new URLSearchParams(window.location.search)
      leadParams.append(
        'utm_15stslocation',
        urlParams.get('utm_15stslocation') || ''
      )

      if (alvinRedirectEnabled) {
        const cplData = translateAnswersToCPLFormat({
          mergedAnswers,
          currentMortgageDetails,
        })
        leadParams.append('customization', 'alvin')
        leadParams.append('alvin-data', JSON.stringify(cplData))
      }

      redirectUrl =
        environment.appEnv === 'prod' ? rate.trackingLink : trackingLinkWithQa
      redirectUrl += '&' + leadParams
    }

    return window.open(redirectUrl, '_blank')
  }

  useEffect(() => {
    if (typeof showCompareDemo === 'boolean' && !showCompareDemo) {
      navigate(`/${Routes.APP_1003}`)
    }
  }, [navigate, showCompareDemo])

  useEffect(() => {
    if (!sortedBankrateRates?.length && bankrateRates?.length > 0) {
      if (showCashOutVariation && equityInHome > 10000) {
        setMaxCashOutAmount(equityInHome)
        setNoRatesVariation('cashOut')
      } else {
        // If we got rate, but filtered all of them out, the user has a great rate
        setNoRatesVariation('greatRates')
      }
    }
  }, [sortedBankrateRates, bankrateRates])

  useEffect(() => {
    async function getRates() {
      try {
        // @ts-ignore
        const ratesResponse = await requestBankrateRates()

        if (!ratesResponse.length) {
          await updateStatus(Status.QNoRatesDisplayed)
        }
      } catch (e) {
        setHasRequestedRates(true)
        await updateStatus(Status.QNoRatesDisplayed)
      }

      setHasRequestedRates(true)
      await updateStatus(Status.QRatesDisplayed)
    }

    getRates()
  }, [])

  if (!hasRequestedRates) {
    return <Loading loaderType="house" shouldWrap={false} />
  }

  return (
    <div className={`rate-selections ${modifierClass || ''}`}>
      {hasRequestedRates && !sortedBankrateRates.length ? (
        <RatesVariation
          variation={noRatesVariation}
          cashOutAmount={maxCashOutAmount}
          submitRate={submitRate}
        />
      ) : (
        <RatesPage submitRate={submitRate} />
      )}
    </div>
  )
}

export default CompareLERateSelection
